import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { firstValueFrom, Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { AuthenticationFacade } from '../../authentication';
import { AppRoute } from '../../enums';
import { ChangePasswordError, ResetPassword, ServerResponseDto } from '../../interfaces';
import { MessageModel } from '../../models';
import { ApiService } from '../api';
import { CredentialsService } from '../credentials/credentials.service';

@Injectable({
  providedIn: 'root',
})
export class AccountService {
  private readonly apiService = inject(ApiService);
  private readonly authenticationFacade = inject(AuthenticationFacade);
  private readonly credentialsService = inject(CredentialsService);
  private readonly router = inject(Router);
  private readonly userUrl = 'users/';

  private get api(): {
    get: <T>(url: string) => Observable<T>;
    post: <T>(url: string, body?: unknown) => Observable<T>;
    put: <T>(url: string, body?: unknown) => Observable<T>;
  } {
    return {
      get: <T>(url: string) => this.apiService.restGet<T>(url),
      post: <T>(url: string, body?: unknown) => this.apiService.post<T>(url, body),
      put: <T>(url: string, body?: unknown) => this.apiService.put<T>(url, body) as Observable<T>,
    };
  }

  public autoChangeEmail(code: string): Observable<ServerResponseDto> {
    return this.api.put<ServerResponseDto>(`${this.userUrl}EmailAutoChange/${code}`);
  }

  public changeEmailForUser(data: {
    newEmail: string;
    emailRepeated: string;
    password: string;
  }): Observable<ServerResponseDto> {
    return this.apiService.post<ServerResponseDto>(`${this.userUrl}changeemail`, {
      email: data.newEmail,
      emailRepeated: data.emailRepeated,
      password: data.password,
    });
  }

  public async changePassword(
    data: { oldPassword: string; password: string; passwordMatch: string },
    preLogoutHook?: () => Promise<void>,
  ): Promise<void> {
    await firstValueFrom(this.apiService.post(`${this.userUrl}changepassword`, data));

    await this.credentialsService.resetPin();
    await preLogoutHook?.();

    // Force a logout so the now-stale session and in-memory credentials are invalidated
    // immediately.
    await this.authenticationFacade.logout();
    await this.router.navigate([AppRoute.Logout]);
  }

  public buildChangePasswordErrorMessage(error: unknown): MessageModel {
    if (typeof error === 'string') {
      return { message: error };
    }

    if (this.isChangePasswordError(error)) {
      const errorConfig: MessageModel = {
        message: error.error,
      };

      if (error.amountOfOldPasswords !== undefined) {
        errorConfig.messageParam = {
          amountOfOldPasswords: error.amountOfOldPasswords,
        };
      }
      return errorConfig;
    }

    throw error;
  }

  public createUser(accountInfo: { email: string; personalNumber: string }): Observable<ServerResponseDto> {
    return this.api
      .post<ServerResponseDto>(`${this.userUrl}`, {
        eMail: accountInfo.email,
        personalNumber: accountInfo.personalNumber.trim(),
      })
      .pipe(
        map((value) => {
          if (value.success === false) {
            throw value;
          }
          return value;
        }),
      );
  }

  public deleteUser(password: string): Observable<ServerResponseDto> {
    return this.apiService.deleteUser(password) as Observable<ServerResponseDto>;
  }

  public getNewPassword(personalNumber: string): Observable<ServerResponseDto> {
    return this.api.post<ServerResponseDto>(`${this.userUrl}${personalNumber}/reset-password`);
  }

  public requestNewValidationLink(pn: string): Observable<string | unknown> {
    return this.api.put(`${this.userUrl}${pn}/reset-activation-code`);
  }

  public sendNewPinForUser(password: string): Observable<boolean> {
    return this.apiService
      .post<boolean>(`${this.userUrl}newpin`, { password })
      .pipe(map((response) => typeof response === 'boolean' && response));
  }

  public setNewPassword(data: ResetPassword): Observable<ServerResponseDto> {
    const { code, password, passwordRepeated, personalNumber } = data;
    return this.api.put<ServerResponseDto>(`${this.userUrl}${personalNumber}/password`, {
      code,
      password,
      passwordRepeated,
    });
  }

  public validateEmailAndSetPassword(validationData: {
    personalNumber: string;
    password: string;
    code: string;
    passwordRepeated: string;
  }): Observable<ServerResponseDto> {
    const path = `${this.userUrl}validate`;
    const payload = {
      personalNumber: validationData.personalNumber.trim(),
      pin: validationData.code.trim(),
      password: validationData.password,
      passwordRepeated: validationData.passwordRepeated,
    };

    return this.api.post<ServerResponseDto>(path, payload);
  }

  private isChangePasswordError(err: unknown): err is ChangePasswordError {
    return (
      typeof err === 'object' &&
      err !== null &&
      'error' in err &&
      typeof (err as ChangePasswordError).error === 'string'
    );
  }
}
